import { z } from "zod";
import { Id } from "./ids.js";

/** UTC instant as ISO-8601 with offset (AGENTS.md §4: timestamps are UTC). */
export const Instant = z.iso.datetime({ offset: true });
export type Instant = z.infer<typeof Instant>;

/** Kitchen business day, ISO date (YYYY-MM-DD). Not a timestamp. */
export const BusinessDate = z.iso.date();
export type BusinessDate = z.infer<typeof BusinessDate>;

/** Optimistic-locking counter on every writable entity (ADR-11). */
export const Version = z.int().nonnegative();

/** Columns every stored entity carries. `archivedAt` replaces deletion (AGENTS.md §3.14). */
export const EntityBase = z.object({
  id: Id,
  version: Version,
  createdAt: Instant,
  updatedAt: Instant,
  archivedAt: Instant.nullable(),
});
export type EntityBase = z.infer<typeof EntityBase>;

const PERSIAN_DIGITS = "۰۱۲۳۴۵۶۷۸۹";
const ARABIC_DIGITS = "٠١٢٣٤٥٦٧٨٩";

/** Converts Persian/Arabic digits to ASCII digits. */
export function toAsciiDigits(input: string): string {
  return input.replace(/[۰-۹٠-٩]/g, (d) => {
    const p = PERSIAN_DIGITS.indexOf(d);
    return String(p >= 0 ? p : ARABIC_DIGITS.indexOf(d));
  });
}

/**
 * Normalises a Persian display name: Arabic ي/ك → Persian ی/ک, ZWNJ kept, whitespace collapsed and trimmed.
 * Excel sheets have trailing spaces and mixed letters (docs/00 §4), so every name goes through this.
 */
export function normalizeName(input: string): string {
  return input
    .replace(/[يى]/g, "ی")
    .replace(/ك/g, "ک")
    .replace(/\s+/g, " ") // \s also matches NBSP and the other Unicode spaces Excel leaves behind
    .trim();
}

/** A human-entered name: normalised, 1–120 characters. */
export const Name = z.string().transform(normalizeName).pipe(z.string().min(1).max(120));

/** Optional free text such as a note or description. */
export const Note = z.string().trim().max(2000);
